import { recognitionMetricKeys, type CaptureProfile, type LocatedMetricRegion, type RecognitionMetricKey } from '@/domain/types'
import type { LayoutDetection, LayoutSearchDiagnostic } from '@/presets/types'
import { rectIntersectionOverUnion } from './adaptive-roi'

export interface CalibrationProgress {
  stage: 'preparing' | 'locating' | 'saving' | 'ended'
  located: RecognitionMetricKey[]
  confirming: RecognitionMetricKey[]
  saved: RecognitionMetricKey[]
  metrics?: readonly RecognitionMetricKey[]
  failed?: RecognitionMetricKey[]
  diagnostics?: LayoutSearchDiagnostic
  confirmation?: 'source-frames' | 'static-structure'
  captureSize?: { width: number; height: number }
}
export interface CalibrationInitializationOptions {
  metrics?: readonly RecognitionMetricKey[]
  action?: 'locate' | 'retry-save'
  signal?: AbortSignal
  /** Reject late saves when the owner has switched or disconnected its capture. */
  isCurrent?: () => boolean
  onProgress?: (progress: CalibrationProgress) => void
  onFrame?: (frame: ImageData) => void
}

export function pendingCalibrationMetrics(profile: CaptureProfile): RecognitionMetricKey[] {
  return recognitionMetricKeys.filter(metric => !profile.roiOverrides[metric] && !profile.autoRois?.[metric])
}

/** Source timestamps normally; explicit static calibration supplies independent sample times. */
export class LayoutConfirmation {
  constructor(readonly replaceMetrics: readonly RecognitionMetricKey[] = []) {}
  private candidates = new Map<RecognitionMetricKey, { region: LocatedMetricRegion; at: number }>()
  private misses = new Map<RecognitionMetricKey, number>()
  private lastObservationAt = -Infinity
  readonly confirmed: LayoutDetection['regions'] = {}
  hud: LayoutDetection['hud']

  observe(detection: LayoutDetection, metrics: readonly RecognitionMetricKey[], at: number): void {
    if (at <= this.lastObservationAt) return
    this.lastObservationAt = at
    for (const metric of metrics) {
      const region = detection.regions[metric]
      if (!region) {
        const misses = (this.misses.get(metric) ?? 0) + 1
        this.misses.set(metric, misses)
        if (misses >= 2 || detection.diagnostics?.reason === 'multiple-scenes') {
          this.candidates.delete(metric); delete this.confirmed[metric]
        }
        continue
      }
      this.misses.delete(metric)
      const previous = this.candidates.get(metric)
      if (previous && at > previous.at && (previous.region.hudVariant ?? 'wide') === (region.hudVariant ?? 'wide')
        && rectIntersectionOverUnion(previous.region.rect, region.rect) >= .9
        && (!region.barRect || previous.region.barRect && rectIntersectionOverUnion(previous.region.barRect, region.barRect) >= .9)) {
        this.confirmed[metric] = region
        if (detection.hud && ['hp', 'mp', 'exp'].includes(metric)) this.hud = detection.hud
      } else if (!previous || at > previous.at) delete this.confirmed[metric]
      this.candidates.set(metric, { region, at })
    }
  }

  /** The save boundary is strict even though intermediate search tolerates one miss. */
  verify(detection: LayoutDetection): void {
    for (const metric of recognitionMetricKeys) {
      const confirmed = this.confirmed[metric], current = detection.regions[metric]
      if (confirmed && (!current || (confirmed.hudVariant ?? 'wide') !== (current.hudVariant ?? 'wide') || rectIntersectionOverUnion(confirmed.rect, current.rect) < .9
        || confirmed.barRect && (!current.barRect || rectIntersectionOverUnion(confirmed.barRect, current.barRect) < .9))) delete this.confirmed[metric]
    }
  }
}

export function applyCalibration(profile: CaptureProfile, result: LayoutConfirmation, overwrite: boolean): CaptureProfile {
  const autoRois = { ...profile.autoRois }, roiOverrides = { ...profile.roiOverrides }
  for (const metric of recognitionMetricKeys) {
    const region = result.confirmed[metric]
    // A manual text crop can still receive a missing, independently verified track.
    const missingManualTrack = roiOverrides[metric] && region?.barRect && !autoRois[metric]?.barRect
    if (!region || !overwrite && !missingManualTrack && !result.replaceMetrics.includes(metric) && (roiOverrides[metric] || autoRois[metric])) continue
    autoRois[metric] = region
    if (overwrite) delete roiOverrides[metric]
  }
  return { ...profile, autoRois, roiOverrides, hudRect: result.hud ?? profile.hudRect, updatedAt: Date.now() }
}

export function calibrationRegionsChanged(previous: CaptureProfile, next: CaptureProfile): RecognitionMetricKey[] {
  return recognitionMetricKeys.filter(metric => JSON.stringify([previous.roiOverrides[metric], previous.autoRois?.[metric]])
    !== JSON.stringify([next.roiOverrides[metric], next.autoRois?.[metric]]))
}
